/*
  ZEVQILO Atelier — global.js
  Small, dependency-free helpers and custom elements used on every page.
  Each element initialises itself in connectedCallback and cleans up in disconnectedCallback,
  so sections re-rendered by the theme editor or the Section Rendering API just work.
*/
(() => {
  const theme = (window.theme = window.theme || {});

  /* Utilities ---------------------------------------------------------- */
  theme.debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  theme.announce = (message) => {
    const region = document.getElementById('zq-live');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  };

  theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  theme.fetchHTML = async (url, { signal } = {}) => {
    const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return theme.parseHTML(await response.text());
  };

  /* A relative URL ("?page=2") is read against the current page, not the site root. */
  theme.sectionUrl = (url, sectionId) => {
    const target = new URL(url, window.location.href);
    target.searchParams.set('section_id', sectionId);
    return target.toString();
  };

  /* Replace an element's children with the matching element from another document. */
  theme.swapFrom = (doc, selector, root = document) => {
    const next = doc.querySelector(selector);
    const current = root.querySelector(selector);
    if (next && current) current.replaceWith(next);
    return next;
  };

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  theme.focusable = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el.getClientRects().length > 0
    );

  /* Dialogs ------------------------------------------------------------ */
  const openDialogs = new Set();

  theme.openDialog = (dialog, trigger = document.activeElement) => {
    if (!dialog || dialog.open) return;
    dialog._returnFocus = trigger instanceof HTMLElement ? trigger : null;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    if (!document.body.classList.contains('is-locked')) {
      const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.documentElement.style.setProperty('--scrollbar-gap', `${gap}px`);
    }
    openDialogs.add(dialog);
    document.body.classList.add('is-locked');
    if (dialog._returnFocus) dialog._returnFocus.setAttribute('aria-expanded', 'true');
    const autofocusTarget = dialog.querySelector('[data-dialog-focus]') || theme.focusable(dialog)[0];
    if (autofocusTarget) autofocusTarget.focus({ preventScroll: true });
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  };

  theme.closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  };

  const handleDialogClosed = (dialog) => {
    openDialogs.delete(dialog);
    if (openDialogs.size === 0) document.body.classList.remove('is-locked');
    const trigger = dialog._returnFocus;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (document.contains(trigger)) trigger.focus({ preventScroll: true });
    }
    dialog._returnFocus = null;
    dialog.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
  };

  document.addEventListener(
    'close',
    (event) => {
      if (event.target instanceof HTMLDialogElement) handleDialogClosed(event.target);
    },
    true
  );

  /* Back/forward cache ---------------------------------------------------
     Browsers restore a page exactly as it was left. A visitor who followed a link from the menu, the
     search or the bag would come back to that panel still open and the page locked behind it.
     Restored pages come back closed and scrollable instead, without replaying the closing slide. */
  theme.resetTransientUI = () => {
    document.querySelectorAll('dialog[open]').forEach((dialog) => {
      dialog.classList.add('is-instant');
      theme.closeDialog(dialog);
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => dialog.classList.remove('is-instant')));
    });
    openDialogs.clear();
    document.body.classList.remove('is-locked');
    document.querySelectorAll('details[data-mega][open]').forEach((menu) => {
      menu.open = false;
    });
    document.querySelectorAll('mobile-menu').forEach((menu) => menu.reset?.());
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) theme.resetTransientUI();
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.querySelector(opener.dataset.dialogOpen);
      if (dialog) {
        event.preventDefault();
        theme.openDialog(dialog, opener);
      }
      return;
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) {
        event.preventDefault();
        theme.closeDialog(dialog);
      }
      return;
    }

    /* Click on the backdrop (outside the dialog box) closes it. */
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) theme.closeDialog(event.target);
    }
  });

  /* Quantity input ----------------------------------------------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.onClick = this.onClick.bind(this);
      this.onChange = this.updateButtons.bind(this);
      this.addEventListener('click', this.onClick);
      this.input?.addEventListener('change', this.onChange);
      this.updateButtons();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.input?.removeEventListener('change', this.onChange);
    }

    onClick(event) {
      const button = event.target.closest('[data-step]');
      if (!button || !this.input) return;
      event.preventDefault();
      const previous = this.input.value;
      const step = Number(button.dataset.step);
      const min = this.input.min === '' ? 0 : Number(this.input.min);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const next = Math.min(max, Math.max(min, (Number(this.input.value) || 0) + step));
      this.input.value = String(next);
      if (previous !== this.input.value) {
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateButtons() {
      if (!this.input) return;
      const value = Number(this.input.value) || 0;
      const min = this.input.min === '' ? 0 : Number(this.input.min);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const minus = this.querySelector('[data-step="-1"]');
      const plus = this.querySelector('[data-step="1"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* Scroll rail (carousel on native scroll-snap) ----------------------- */
  class ScrollRail extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    /* Safe to call again when the track is added later (e.g. lazy recommendations). */
    init() {
      this.disconnectedCallback();
      this.track = this.querySelector('[data-rail-track]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.track) return;
      this.onPrev = () => this.scrollByPage(-1);
      this.onNext = () => this.scrollByPage(1);
      this.onScroll = theme.debounce(() => this.update(), 60);
      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    scrollByPage(direction) {
      const item = this.track.firstElementChild;
      const itemWidth = item ? item.getBoundingClientRect().width : this.track.clientWidth;
      const perPage = Math.max(1, Math.floor(this.track.clientWidth / itemWidth));
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: direction * perPage * (itemWidth + gap), behavior: reduce ? 'auto' : 'smooth' });
    }

    update() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      const scrollable = max > 0;
      if (this.prev) this.prev.disabled = !scrollable || this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = !scrollable || this.track.scrollLeft >= max;
      this.toggleAttribute('data-scrollable', scrollable);
    }
  }
  customElements.define('scroll-rail', ScrollRail);

  /* Announcement bar rotation -----------------------------------------
     All messages share one grid cell, so the bar is always as tall as its longest message: the page
     below never moves when the message changes, even when one message wraps onto two lines on a
     small phone. Messages that are not showing are hidden from view, screen readers and the keyboard. */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-announcement]'));
      this.index = 0;
      this.paused = false;
      if (this.items.length < 2) return;
      this.classList.add('is-stacked');
      this.items.forEach((item, index) => this.setActive(item, index === 0));
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.pause = () => (this.paused = true);
      this.resume = () => (this.paused = false);
      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);
      const delay = (Number(this.dataset.speed) || 5) * 1000;
      this.timer = window.setInterval(() => {
        if (!this.paused && !document.hidden) this.show(this.index + 1);
      }, delay);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      this.removeEventListener('mouseenter', this.pause);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('focusin', this.pause);
      this.removeEventListener('focusout', this.resume);
    }

    setActive(item, active) {
      item.hidden = false;
      item.classList.toggle('is-active', active);
      item.classList.toggle('is-inactive', !active);
      item.inert = !active;
      if (active) {
        item.removeAttribute('aria-hidden');
      } else {
        item.setAttribute('aria-hidden', 'true');
      }
    }

    show(index) {
      this.setActive(this.items[this.index], false);
      this.index = (index + this.items.length) % this.items.length;
      this.setActive(this.items[this.index], true);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* Read more: clamps long text and reveals the toggle only when needed --- */
  class ReadMore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-read-more-content]');
      this.toggle = this.querySelector('[data-read-more-toggle]');
      if (!this.content || !this.toggle) return;
      this.content.classList.add('is-clamped');
      this.onToggle = this.onToggle.bind(this);
      this.measure = this.measure.bind(this);
      this.toggle.addEventListener('click', this.onToggle);
      /* Content inside a closed accordion has no height yet: measure again when it opens. */
      this.details = this.closest('details');
      this.details?.addEventListener('toggle', this.measure);
      window.requestAnimationFrame(this.measure);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.onToggle);
      this.details?.removeEventListener('toggle', this.measure);
    }

    measure() {
      if (this.toggle.getAttribute('aria-expanded') === 'true' || !this.content.clientHeight) return;
      const overflowing = this.content.scrollHeight > this.content.clientHeight + 4;
      this.toggle.hidden = !overflowing;
      this.content.classList.toggle('is-clamped', overflowing);
    }

    onToggle() {
      const expanded = this.toggle.getAttribute('aria-expanded') === 'true';
      this.toggle.setAttribute('aria-expanded', String(!expanded));
      this.content.classList.toggle('is-clamped', expanded);
      this.toggle.querySelector('[data-more]').hidden = !expanded;
      this.toggle.querySelector('[data-less]').hidden = expanded;
    }
  }
  if (!customElements.get('read-more')) customElements.define('read-more', ReadMore);

  /* Header: hide on scroll down, reveal on scroll up; mega menus ------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.menus = Array.from(this.querySelectorAll('details[data-mega]'));
      this.hoverTimers = new Map();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);

      this.menus.forEach((menu) => {
        menu.addEventListener('toggle', () => {
          if (menu.open) this.closeMenus(menu);
          this.classList.toggle('has-open-menu', this.menus.some((m) => m.open));
        });
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          menu.querySelector('summary')?.addEventListener('click', (event) => {
            /* A click right after hover-open should not immediately close the panel. */
            if (menu.open && Date.now() - (menu._hoverOpenedAt || 0) < 500) event.preventDefault();
          });
          menu.addEventListener('mouseenter', () => {
            clearTimeout(this.hoverTimers.get(menu));
            this.hoverTimers.set(
              menu,
              setTimeout(() => {
                if (!menu.open) menu._hoverOpenedAt = Date.now();
                menu.open = true;
              }, 80)
            );
          });
          menu.addEventListener('mouseleave', () => {
            clearTimeout(this.hoverTimers.get(menu));
            this.hoverTimers.set(menu, setTimeout(() => (menu.open = false), 160));
          });
        }
      });

      /* A page restored part-way down (back button, reload) starts in the scrolled state. */
      this.classList.toggle('is-scrolled', window.scrollY > 8);
      this.setHeightVar();
      this.resizeObserver = new ResizeObserver(() => this.setHeightVar());
      this.resizeObserver.observe(this);
      /* The announcement bar shares the header group; its height counts towards the first screen. */
      document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => this.resizeObserver.observe(section));
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      this.resizeObserver?.disconnect();
      this.hoverTimers.forEach((timer) => clearTimeout(timer));
    }

    setHeightVar() {
      const root = document.documentElement;
      /* Kept here so scrolling never has to measure the header. */
      this.height = this.offsetHeight;
      /* How much of the top of the screen the header covers while scrolling: nothing when it is set
         not to stick (sticky panels and links to a part of the page then use the full height). */
      const sticky = !this.classList.contains('site-header--static');
      root.style.setProperty('--header-offset', `${sticky ? this.height : 0}px`);
      /* Everything above the page content (announcement bar and header): a full screen hero fills the rest. */
      const group = Array.from(document.querySelectorAll('.shopify-section-group-header-group')).reduce((sum, section) => sum + section.offsetHeight, 0);
      root.style.setProperty('--header-group-height', `${Math.max(group, this.offsetHeight)}px`);
    }

    onScroll() {
      const y = window.scrollY;
      if (this.menus.some((m) => m.open)) {
        this.lastY = y;
        return;
      }
      const threshold = (this.height || 0) * 2;
      if (y > threshold && y > this.lastY + 4) {
        this.setHidden(true);
      } else if (y < this.lastY - 4 || y <= threshold) {
        this.setHidden(false);
      }
      this.classList.toggle('is-scrolled', y > 8);
      this.lastY = y;
    }

    /* The header slides away while the visitor reads down the page and comes back on the way up.
       html.header-hidden lets bars that stick below it (filter and sort) move up and down with it. */
    setHidden(hidden) {
      if (this.classList.contains('is-hidden') === hidden) return;
      this.classList.toggle('is-hidden', hidden);
      document.documentElement.classList.toggle('header-hidden', hidden);
    }

    closeMenus(except) {
      this.menus.forEach((menu) => {
        if (menu !== except) menu.open = false;
      });
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const open = this.menus.find((m) => m.open);
      if (open) {
        open.open = false;
        open.querySelector('summary')?.focus();
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.closeMenus();
    }
  }
  customElements.define('site-header', SiteHeader);

  /* Mobile menu: second level slides over the first ---------------------
     A second-level panel covers exactly the visible part of the list: the list goes back to its top
     underneath and cannot be scrolled while the panel is open, so the panel never drifts with it. The
     menu starts again from the first level each time it opens. */
  class MobileMenu extends HTMLElement {
    connectedCallback() {
      this.dialog = this.closest('dialog');
      this.listScroll = 0;
      this.onClick = this.onClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.addEventListener('click', this.onClick);
      this.dialog?.addEventListener('close', this.onClose);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.dialog?.removeEventListener('close', this.onClose);
    }

    onClick(event) {
      const open = event.target.closest('[data-submenu-open]');
      const back = event.target.closest('[data-submenu-back]');
      if (open) {
        this.openPanel(open);
      } else if (back) {
        this.closePanel(back.closest('[data-submenu]'), true);
      }
    }

    openPanel(trigger) {
      const panel = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
      if (!panel) return;
      this.listScroll = this.scrollTop;
      this.scrollTop = 0;
      this.classList.add('has-submenu');
      panel.hidden = false;
      panel.scrollTop = 0;
      trigger.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => panel.classList.add('is-active'));
      /* The panel is still off to the side: focusing must not scroll anything towards it. */
      panel.querySelector('[data-submenu-back]')?.focus({ preventScroll: true });
    }

    closePanel(panel, returnFocus) {
      if (!panel) return;
      const trigger = this.querySelector(`[aria-controls="${panel.id}"]`);
      panel.classList.remove('is-active');
      panel.hidden = true;
      trigger?.setAttribute('aria-expanded', 'false');
      this.classList.remove('has-submenu');
      this.scrollTop = this.listScroll;
      if (returnFocus) trigger?.focus();
    }

    /* Back to the first level, at the top. */
    reset() {
      this.querySelectorAll('[data-submenu]:not([hidden])').forEach((panel) => this.closePanel(panel, false));
      this.listScroll = 0;
      this.scrollTop = 0;
    }

    /* After the drawer has slid away, so nothing changes while it is still in view. */
    onClose() {
      const sliding = typeof this.dialog.getAnimations === 'function' ? this.dialog.getAnimations().map((animation) => animation.finished) : [];
      Promise.allSettled(sliding).then(() => {
        if (!this.dialog.open) this.reset();
      });
    }
  }
  customElements.define('mobile-menu', MobileMenu);

  /* Scroll reveals and other motion: assets/motion.js (loaded when Theme settings → Motion is on). */

  /* Share button -------------------------------------------------------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.onClick = this.onClick.bind(this);
      this.button?.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
    }

    async onClick() {
      const data = { title: this.dataset.title || document.title, url: this.dataset.url || window.location.href };
      if (navigator.share) {
        try {
          await navigator.share(data);
        } catch (error) {
          /* The visitor closed the share sheet. */
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(data.url);
        theme.announce(theme.strings.copied);
        const status = this.querySelector('[data-share-status]');
        if (status) {
          status.hidden = false;
          window.setTimeout(() => (status.hidden = true), 2500);
        }
      } catch (error) {
        window.prompt('', data.url);
      }
    }
  }
  customElements.define('share-button', ShareButton);

  /* Filter-as-you-type list (FAQ) --------------------------------------- */
  class FilterList extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-filter-input]');
      this.items = Array.from(this.querySelectorAll('[data-filter-item]'));
      this.groups = Array.from(this.querySelectorAll('[data-filter-group]'));
      this.empty = this.querySelector('[data-filter-empty]');
      this.onInput = theme.debounce(() => this.filter(), 120);
      this.input?.addEventListener('input', this.onInput);
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this.onInput);
    }

    filter() {
      const query = this.input.value.trim().toLowerCase();
      let visible = 0;
      this.items.forEach((item) => {
        const match = !query || item.textContent.toLowerCase().includes(query);
        item.hidden = !match;
        if (match) visible += 1;
      });
      this.groups.forEach((group) => {
        group.hidden = !group.querySelector('[data-filter-item]:not([hidden])');
      });
      if (this.empty) this.empty.hidden = visible > 0;
    }
  }
  customElements.define('filter-list', FilterList);
})();
